import type { BuilderAccount } from "api-types/common/responseTypes";
import type { ClientHomePlanPublic } from "api-types/homePlans/homePlanResponses";
import { WEB_API_BASE_PATH } from "common/constants";
import BuilderPortalEmptyCTA from "lib/components/builders/BuilderPortalEmptyCTA";
import { useFetch } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useMemo } from "react";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column, PaginationState } from "ui/tables/DataTable/types";
import { buildCustomFieldColumns } from "./customFieldColumns";
import PublicHomePlanDropdown from "./PublicHomePlanDropdown";

interface PublicHomePlanTableProps {
  homePlans: ClientHomePlanPublic[];
  onDelete: (id: string) => void;
  totalCount: number;
  pagination: PaginationState;
  setPagination: (pagination: PaginationState) => void;
}

export default function PublicHomePlansTable({
  homePlans,
  onDelete,
  totalCount,
  pagination,
  setPagination,
}: PublicHomePlanTableProps) {
  const { builderId } = useAppRouterParams();
  const { data: builderData } = useFetch<BuilderAccount>(`${WEB_API_BASE_PATH}/builders/${builderId}/account`, [
    builderId,
  ]);
  const customFields = builderData?.builder?.salesConfig?.publicShowroomCTA?.customFields;

  const columns: Column<ClientHomePlanPublic>[] = useMemo(() => {
    const cols: Column<ClientHomePlanPublic>[] = [
      {
        header: "Plan ID",
        accessor: (hp) => hp?.planName,
        fallback: true,
        bold: true,
      },
      {
        header: "Customer",
        accessor: (hp) => hp?.customerInfo?.name,
        fallback: true,
      },
      {
        header: "Master Plan",
        field: "masterPlanName",
        fallback: true,
      },
      {
        header: "Price",
        accessor: (hp) => (!hp?.stats?.totalPrice ? null : hp?.stats?.totalPrice),
        type: "USD",
        fallback: true,
      },
      {
        header: "Created",
        field: "createdAt",
        type: "DATE",
      },
    ];
    cols.splice(
      2,
      0,
      ...buildCustomFieldColumns<ClientHomePlanPublic>(customFields, (hp) => hp.customerCustomFields, { hide: true }),
    );
    return cols;
  }, [customFields]);

  const data: ClientHomePlanPublic[] = useMemo(() => homePlans ?? [], [homePlans]);

  return (
    <ConfigTable
      columns={columns}
      showHideColumns
      showHideColumnsStorageKey="customer-plans-column-visibility"
      data={data}
      overflowMenu={(row) => <PublicHomePlanDropdown homePlan={row.original} onDelete={onDelete} />}
      emptyCTA={
        <div style={{ marginTop: 48 }}>
          <BuilderPortalEmptyCTA>
            <div className="cta-container">
              <div className="cta-title">No Customer Plans</div>
              <div className="cta-text">All Home Plans have been assigned to a Location.</div>
            </div>
          </BuilderPortalEmptyCTA>
        </div>
      }
      totalCount={totalCount}
      pagination={pagination}
      setPagination={setPagination}
    />
  );
}
